import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { SharedModule } from '../../shared/shared.module';

import {
  NavigationItem,
  navigationItemsNoPatient,
} from '../../models/navigation-item-model';
import { classes, pages } from '../../shared/labels';
import { LabelAndDescription } from '../../models/patient-model';
import { LabelService } from '../../services/label.service';
import { DoctorService } from '../../services/doctor.service';
import { Module } from '../../models/doctor-model';
import { Subject, takeUntil } from 'rxjs';
import { ErrorPanelService } from '../../services/error-panel.service';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipListboxChange, MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationPanelComponent } from '../../shared/confirmation-panel/confirmation-panel.component';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-documents-page',
  standalone: true,
  imports: [
    SharedModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    MatTooltipModule,
    MatChipsModule,
    FormsModule,
  ],
  templateUrl: './documents-page.component.html',
  styleUrl: './documents-page.component.scss',
})
export class DocumentsPageComponent implements OnInit {
  navigationItems: NavigationItem[];

  showAddDocumentPanel: boolean = false;
  showDeleteConfirmationPanel: boolean = false;
  showModifyModulePanel: boolean = false;

  inputBoxesForNewModule: LabelAndDescription[] = [];
  inputBoxesForModifyModule: LabelAndDescription[] = [];

  displayedColumns: string[] = ['category', 'title', 'format', 'actions'];

  // todo: get the correct categories
  categories: string[] = ['Modulistica', 'Testistica', 'Didattica', 'Altro'];
  selectedChips: string[] = ['Tutte le categorie'];

  modules: Module[] = [];
  selectedModules: Module[] = [];
  selectedModule: Module = {
    id: '',
    category: '',
    title: '',
    date_added: '',
    file_content_type: '',
  };

  isLoading: boolean = true;

  constructor(
    public labelService: LabelService,
    private doctorService: DoctorService,
    public errorPanelService: ErrorPanelService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog
  ) {
    // highlight the correct page in the side bar
    this.navigationItems = navigationItemsNoPatient.map((item) => {
      if (item.label === pages.modules) {
        return { ...item, class: classes.hb };
      }
      return { ...item };
    });
  }

  ngOnInit(): void {
    this.getDocuments();
  }

  getDocuments() {
    this.isLoading = true;
    this.modules = [];
    this.selectedModules = [];

    this.doctorService.getModules().subscribe({
      next: (response) => {
        response.map((doc: Module) => {
          this.modules.push(doc);
          this.selectedModules.push(doc);
        });
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Errore nel recuperare i documenti', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore nel recuperare i documenti'
        );
        this.isLoading = false;
      },
    });
  }

  toggleAddDocumentPanel() {
    this.showAddDocumentPanel = !this.showAddDocumentPanel;

    if (this.showAddDocumentPanel) {
      var obj: LabelAndDescription[] = [
        { label: 'title', description: '' },
        { label: 'category', description: '' },
      ];
      this.inputBoxesForNewModule = obj;
    }
  }
  confirmNewModule(event: any) {
    const moduleData = Object.fromEntries(
      event.inputs.map((field: { label: string; description: string }) => [
        field.label,
        field.description,
      ])
    );

    this.doctorService
      .createNewModule(JSON.stringify(moduleData), event.file)
      .subscribe({
        next: (response) => {
          this.toggleAddDocumentPanel();
          this.getDocuments();
        },
        error: (error) => {
          console.error('Errore nel caricare il nuovo documento', error);
          this.errorPanelService.showErrorSnackBar(
            'Errore nel caricare il nuovo documento'
          );
        },
      });
  }

  onChipSelectionChange(event: MatChipListboxChange): void {
    const value = event.value as string[];

    // If no chips are selected, select 'all'
    if (value.length === 0) {
      this.selectedChips = ['Tutte le categorie'];
      this.filterInvoicesByChips();
      return;
    }

    if (value.length > 0) {
      if (value.includes('Tutte le categorie')) {
        this.selectedChips = value.filter(
          (chip) => chip !== 'Tutte le categorie'
        );
      } else {
        this.selectedChips = value;
      }
      this.filterInvoicesByChips();
      return;
    }
  }

  private filterInvoicesByChips(): void {
    if (this.selectedChips.includes('Tutte le categorie')) {
      this.selectedModules = this.modules;
      return;
    }

    this.selectedModules = this.modules.filter((module) => {
      return this.selectedChips.every((chip) => {
        switch (chip) {
          case 'Modulistica':
            return module.category === 'Modulistica';
          case 'Testistica':
            return module.category === 'Testistica';
          case 'Didattica':
            return module.category === 'Didattica';
          case 'Altro':
            return module.category === 'Altro';
          default:
            return true;
        }
      });
    });
  }

  onViewDocument(module: Module) {
    this.doctorService.getDocumentFile(module.id).subscribe({
      next: (response) => {
        // Crea un URL dal Blob
        const blob = new Blob([response], { type: module.file_content_type });
        const url = window.URL.createObjectURL(blob);

        // Apri il documento in una nuova scheda
        const newTab = window.open(url, '_blank');
        if (!newTab) {
          window.alert(
            'Impossibile aprire il documento in una nuova scheda. Assicurati che il popup blocker sia disabilitato.'
          );
        }

        // Ritarda la revoca dell'URL per dare tempo alla nuova scheda di caricare il documento
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000); // Ritardo di 1000ms (1 secondo)
      },
      error: (error) => {
        console.error('Errore durante il download del documento:', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore nel download del documento'
        );
      },
    });
  }

  onDownloadDocument(module: Module) {
    this.doctorService.getDocumentFile(module.id).subscribe({
      next: (response) => {
        // Crea un URL dal Blob
        const blob = new Blob([response], { type: module.file_content_type });
        const url = window.URL.createObjectURL(blob);

        // Crea un elemento <a> per scaricare il file
        const a = document.createElement('a');
        a.href = url;
        a.download = module.title;
        document.body.appendChild(a);
        a.click();

        // Pulisci l'URL e rimuovi l'elemento <a> dopo il download
        window.URL.revokeObjectURL(url);
        a.remove();
      },
      error: (error) => {
        console.error('Errore durante il download del documento:', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore nel download del documento'
        );
      },
    });
  }

  deleteModule(module: Module) {
    const dialogRef = this.dialog.open(ConfirmationPanelComponent, {
      data: {
        title: 'Sei sicuro di voler eliminare questo documento?',
        infoText: "L'operazione è irreversibile.",
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.doctorService.deleteModule(module.id).subscribe({
          next: (response) => {
            this.getDocuments();
          },
          error: (error) => {
            console.error('Errore con la eliminazione del documento', error);
            this.errorPanelService.showErrorSnackBar(
              'Errore nella eliminazione del documento'
            );
          },
        });
      }
    });
  }

  toggleModifyPanel(module?: Module) {
    this.showModifyModulePanel = !this.showModifyModulePanel;
    if (module) {
      this.selectedModule = module;
      var obj: LabelAndDescription[] = [
        { label: 'category', description: module.category },
        { label: 'title', description: module.title },
      ];
      this.inputBoxesForModifyModule = obj;
    } else {
      this.selectedModule = {
        id: '',
        category: '',
        title: '',
        date_added: '',
        file_content_type: '',
      };
      this.inputBoxesForModifyModule = [];
    }
  }

  modifyModule(event: any) {
    const moduleData = Object.fromEntries(
      event.inputs.map((field: { label: string; description: string }) => [
        field.label,
        field.description,
      ])
    );

    this.doctorService.putModule(this.selectedModule.id, moduleData).subscribe({
      next: (response) => {
        this.toggleModifyPanel();
        this.getDocuments();
      },
      error: (error) => {
        console.error('Errore nella modifica del documento', error);
        this.toggleModifyPanel();
        this.errorPanelService.showErrorSnackBar(
          'Errore nella modifica del documento'
        );
      },
    });
  }

  handleMenuAction(action: string, module: Module) {
    switch (action) {
      case 'preview':
        if (module.file_content_type === 'application/pdf') {
          this.onViewDocument(module);
        }
        break;
      case 'download':
        this.onDownloadDocument(module);
        break;
      case 'edit':
        this.toggleModifyPanel(module);
        break;
      case 'delete':
        this.deleteModule(module);
        break;
    }
  }

  displayFileType(fileType: string) {
    switch (fileType) {
      case 'application/pdf':
        return 'PDF';
      case 'application/msword':
        return 'DOC';
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'DOCX';
      case 'application/x-iwork-pages-sffpages':
        return 'PAGES';
      default:
        return fileType;
    }
  }
}
